// The trending page. eb-ui served `/trending/searches/:location` via custom SSR with
// all data injected by the Django backend as window.__SERVER_DATA__. Here we fetch it
// in getServerSideProps: trending searches (Core /api/v3/trending/), the slug's display
// name (geo-service-http), and the header/footer chrome. basePath `/trending/searches`
// + this `[location]` file preserves the eb-ui URL for SEO parity.

// Deep-subpath import: the `@eventbrite/webapp-constants@1.0.1` barrel
// (`dist/index.d.ts`) re-exports from a non-existent `lib/` path, so its types
// don't resolve; the `dist/*` modules are intact.
import { EventbriteHeader } from '@eventbrite/webapp-constants/dist/headers';
import { GlobalHeader, type GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import { getGlobalFooterServerSideProps } from '@eventbrite/legacy-infra.site-footer/server';
import type { GlobalFooterProps } from '@eventbrite/legacy-infra.site-footer';
import { OpenGraphTags, TwitterTags } from '@eventbrite/webapp-public-content';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import type { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import React from 'react';

import { fetchTrendingSearchesServer, type TrendingSearch } from '@/api/trendingSearches';
import { getTrendingFixture } from '@/api/fixtures';
import { formatDisplayLocation, getPlaceFromLocationSlug } from '@/api/geoPlace';
import { getEbDomain } from '@/api/coreHost';
import { Footer, type FooterProps } from '@/components/Footer';
import TrendingApp from '@/components/TrendingApp';
import { createDefaultLocaleInfo } from '@/lib/localeInfo';
import { Timeframe, TrendingFeatureFlags } from '@/types';
import { formatPageTitle, formatTrendingSearchPageTitle } from '@/utils';

const DEFAULT_TIMEFRAME: Timeframe = 'month';

interface TrendingPageProps {
    location: string;
    displayLocation: string;
    activeTimeframe: Timeframe;
    trendingSearches: TrendingSearch[];
    degradedTrending: boolean;
    featureFlags: TrendingFeatureFlags;
    shouldNoFollowLinks: boolean;
    localeSubdirectory: string;
    httpReferer: string;
    serverUrl: string;
    globalHeaderProps: GlobalHeaderProps;
    footerProps: FooterProps;
}

/** Drop every `undefined` leaf so getServerSideProps props are JSON-serializable. */
function jsonSafe<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

async function resolveDisplayLocation(
    slug: string,
    localeUnderscore: string,
): Promise<string | null> {
    if (slug.toLowerCase() === 'online') return 'Online';
    const place = await getPlaceFromLocationSlug(slug, localeUnderscore);
    if (!place) return null;
    return formatDisplayLocation(place) || slug;
}

// Best-effort display name from the slug when geo-service is unreachable
// (e.g. "tn--nashville" -> "Nashville, TN"). Not authoritative — a degraded label so a
// display-only lookup never fails the page.
function fallbackDisplayFromSlug(slug: string): string {
    const title = (s: string) =>
        s.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
    const [region, ...rest] = slug.split('--');
    if (rest.length) {
        const label = region.length <= 3 ? region.toUpperCase() : title(region);
        return `${title(rest.join('--'))}, ${label}`;
    }
    return title(slug);
}

// Ensure a thrown value is a plain Error with a WRITABLE message. A fetch AbortError is a
// DOMException whose `message` is getter-only, which crashes Next's error enrichment.
function toError(reason: unknown): Error {
    if (reason instanceof Error && !(reason instanceof DOMException)) return reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    return Object.assign(new Error(msg), { cause: reason });
}

// ---- Fixtures mode (TRENDING_FIXTURES=1) -----------------------------------------
// Renders the REAL page tree with no creds/VPN so the smoke-render gate can run.
// JSON-safe (no undefined). Flag off -> the timeframe-nav + SearchResults path.
function getFixtureProps(slug: string): TrendingPageProps {
    const origin = 'https://www.eventbrite.com';
    // Canonical/OG URL = origin + page path; chrome (header/footer) gets the origin only.
    const serverUrl = `${origin}/trending/searches/${slug}/`;
    return {
        location: slug,
        displayLocation: 'Nashville, TN',
        activeTimeframe: DEFAULT_TIMEFRAME,
        trendingSearches: getTrendingFixture(DEFAULT_TIMEFRAME),
        degradedTrending: false,
        featureFlags: {},
        shouldNoFollowLinks: false,
        localeSubdirectory: '',
        httpReferer: '',
        serverUrl,
        globalHeaderProps: { env: { serverUrl: origin }, request: { path: `/trending/searches/${slug}/` } },
        footerProps: {
            serverUrl: origin,
            localeInfo: createDefaultLocaleInfo(),
            isUserAuthenticated: false,
            globalFooterProps: {
                thirdFooterColumnName: 'Find Events',
                thirdFooterColumn: [],
                popularSearches: [],
                eventsToPlan: [],
                showMinimalLinks: false,
                impressumHelpLink: '',
            },
        },
    };
}

export const getServerSideProps: GetServerSideProps<TrendingPageProps> = async (
    context: GetServerSidePropsContext,
) => {
    const slug = String(context.params?.location || '');
    if (!slug) return { notFound: true };

    if (process.env.TRENDING_FIXTURES === '1') {
        return { props: jsonSafe(getFixtureProps(slug)) };
    }

    const { req, res } = context;
    const localeUnderscore = getLocaleFromRequest(req);
    const ebDomain = getEbDomain(req);
    // Site origin (always the www host, not internal-api) — this is what the site chrome
    // (footer locale-change endpoint, SiteFooter/GlobalHeader base) must use.
    const origin = `https://www.${ebDomain}`;
    // Public-facing canonical/OG URL = origin + the page PATH only. Strip the query string
    // so UTM/tracking params aren't baked into the canonical link or OG url (that splits
    // SEO identity across every ?utm_* variant of this public trending page).
    const pathname = (req.url || '').split('?')[0];
    const serverUrl = `${origin}${pathname}`;

    // Trending searches are the CRITICAL content (fail loud on error). The geo
    // display-name lookup is a nice-to-have: geo-service-http is a VPC-internal ALB,
    // unreachable from local dev and not worth 500ing a public SEO page over — so degrade
    // to a slug-derived label if it fails. `allSettled` (not `Promise.all`) so a geo abort
    // does not orphan the still-in-flight trending rejection (an orphaned DOMException on
    // abort surfaces as the cryptic "Cannot set property message" unhandledRejection).
    const [trendingResult, geoResult] = await Promise.allSettled([
        fetchTrendingSearchesServer(slug, DEFAULT_TIMEFRAME, req),
        resolveDisplayLocation(slug, localeUnderscore),
    ]);

    if (trendingResult.status === 'rejected') {
        throw toError(trendingResult.reason); // fail loud on the real content
    }
    const trendingResponse = trendingResult.value;

    let displayLocation: string;
    if (geoResult.status === 'fulfilled') {
        if (geoResult.value === null) return { notFound: true }; // slug genuinely unresolvable
        displayLocation = geoResult.value;
    } else {
        // geo unreachable/aborted -> degrade, don't crash.
        console.warn(
            `[trending] geo display-name lookup failed for slug=${slug}; using fallback label:`,
            toError(geoResult.reason).message,
        );
        displayLocation = fallbackDisplayFromSlug(slug);
    }

    const trendingSearches = trendingResponse.trending.trending_searches || [];
    // Validate Core's active_timeframe rather than trusting the cast: an unexpected value
    // reaches SearchResults, where timeframesMap[tf] is undefined and .toLowerCase() throws
    // during render (breaking the page, esp. the empty-state). Fall back to the default —
    // the same contract the BFF (src/pages/api/trending.page.ts) enforces.
    const VALID_TIMEFRAMES: readonly Timeframe[] = ['day', 'week', 'month', 'year'];
    const rawActiveTimeframe = trendingResponse.trending.active_timeframe;
    const activeTimeframe: Timeframe = VALID_TIMEFRAMES.includes(
        rawActiveTimeframe as Timeframe,
    )
        ? (rawActiveTimeframe as Timeframe)
        : DEFAULT_TIMEFRAME;

    // Non-critical chrome: degrade to minimal/empty if a fetch fails (Promise.allSettled).
    const [headerResult, footerResult] = await Promise.allSettled([
        getGlobalHeaderServerSideProps(context),
        getGlobalFooterServerSideProps(context),
    ]);

    const globalHeaderProps: GlobalHeaderProps =
        headerResult.status === 'fulfilled'
            ? headerResult.value
            : {
                  env: { serverUrl: origin },
                  request: {
                      path: req.url || `/${slug}/`,
                      session_id: (req.headers[EventbriteHeader.EB_SESSION_ID] as string) || '',
                  },
              };

    const emptyGlobalFooter: GlobalFooterProps = {
        thirdFooterColumnName: 'Find Events',
        thirdFooterColumn: [],
        popularSearches: [],
        eventsToPlan: [],
        showMinimalLinks: false,
        impressumHelpLink: '',
    };
    const globalFooterProps: GlobalFooterProps =
        footerResult.status === 'fulfilled'
            ? {
                  ...footerResult.value,
                  thirdFooterColumn: footerResult.value.thirdFooterColumn ?? [],
                  popularSearches: footerResult.value.popularSearches ?? [],
                  eventsToPlan: footerResult.value.eventsToPlan ?? [],
              }
            : emptyGlobalFooter;

    // The global header + the footer's marketing links vary by auth (see globalHeaderProps
    // and isUserAuthenticated below), so the SSR HTML is personalized. A SHARED CDN cache
    // (s-maxage) could then serve one user's chrome to another. Only shared-cache the
    // anonymous response — identical for every logged-out visitor (the SEO/perf case that
    // matters); serve authenticated requests privately, never from a shared cache.
    const isAuthenticated = !!(req.headers[EventbriteHeader.EB_USER_ID] as string);
    // A degraded upstream response (transient 502/503/504 swallowed -> empty list)
    // must NOT be shared-cached: stale-while-revalidate/stale-if-error would pin the
    // empty page over the last-good copy, and s-maxage could serve it to every
    // visitor for 10 min. Degraded pages are private + short-TTL; normal responses
    // keep the shared-cache policy.
    const isDegraded = trendingResponse._degraded === true;
    res.setHeader(
        'Cache-Control',
        isAuthenticated || isDegraded
            ? 'private, no-store'
            : 'max-age=300, s-maxage=600, stale-while-revalidate=3600, stale-if-error=86400',
    );

    // Search-result links carry a language subdirectory for locales that use one
    // (eb-ui derived this from localeInfo.uses_language_subdirectory — see legacy/app.tsx);
    // e.g. fr_CA -> "/fr_CA", en_US -> "". Hardcoding "" sent localized visitors to the
    // wrong /d/... search URLs.
    const footerLocaleInfo = createDefaultLocaleInfo(req);
    const localeUsesSubdir = Boolean(footerLocaleInfo.uses_language_subdirectory);
    const localeForSubdir =
        typeof footerLocaleInfo.locale === 'string' ? footerLocaleInfo.locale : '';
    const localeSubdirectory = localeUsesSubdir && localeForSubdir ? `/${localeForSubdir}` : '';

    const props: TrendingPageProps = {
        location: slug,
        displayLocation,
        activeTimeframe,
        trendingSearches,
        degradedTrending: trendingResponse._degraded === true,
        featureFlags: {}, // faithful default: flag off (control render path)
        shouldNoFollowLinks: false,
        localeSubdirectory,
        httpReferer: (req.headers.referer as string) || '',
        serverUrl,
        globalHeaderProps,
        footerProps: {
            serverUrl: origin,
            localeInfo: footerLocaleInfo,
            isUserAuthenticated: isAuthenticated,
            globalFooterProps,
        },
    };

    return { props: jsonSafe(props) };
};

export default function TrendingLocationPage(props: TrendingPageProps) {
    const { featureFlags, displayLocation, activeTimeframe, serverUrl, globalHeaderProps, footerProps } =
        props;

    const pageTitle = featureFlags.launchConsolidateAndExpandTrendingSearches
        ? formatTrendingSearchPageTitle(displayLocation)
        : formatPageTitle(displayLocation, activeTimeframe);

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <link rel="canonical" href={serverUrl} />
            </Head>
            <OpenGraphTags url={serverUrl} title={pageTitle} description={pageTitle} />
            <TwitterTags title={pageTitle} description={pageTitle} />

            <GlobalHeader {...globalHeaderProps} />

            <main className="trending-main">
                <TrendingApp
                    activeTimeframe={props.activeTimeframe}
                    trendingSearches={props.trendingSearches}
                    degradedTrending={props.degradedTrending}
                    locationStringForUrl={props.location}
                    displayLocation={props.displayLocation}
                    httpReferer={props.httpReferer}
                    featureFlags={props.featureFlags}
                    shouldNoFollowLinks={props.shouldNoFollowLinks}
                    localeSubdirectory={props.localeSubdirectory}
                />
            </main>

            <Footer {...footerProps} />
        </>
    );
}
